import styled, { css } from 'styled-components';
import { Pages, Testimonial } from '../../assets/ts/customTypes';
import { MainContainer } from '../../assets/ts/layout/main';
import { PageTitle } from '../components/PageTitle';
import { COLOR_CI_BLUE, COLOR_GREY, COLOR_GREY_BRIGHTER } from '../../config';
import { ArrowContentBox, ArrowContentBoxFooter, ArrowContentBoxInner, ArrowContentBoxQuote } from '../components/ArrowContentBox';
import { NameTag } from '../components/NameTag';
import { FooterContainer } from '../components/FooterContainer';
import { LandingpageButton } from '../components/LandingpageButton';
import { defaultFadeInAnimation, defaultScaleUpAnimation, defaultSlideLeftAnimation, defaultSlideUpAnimation } from '../utils/AnimationUtils';
import { useLanguage } from '../contexts/LanguageContext';

function UnstyledTestimonialImage(props: {imageUrl: string, className?: string}) {
    return <svg viewBox="-5 -10 210 240" className={props.className}>
        <mask id="testimonial-image-mask" fill="white">
            <path d="M92.79,4.551C94.209,3.737 96.531,3.737 97.951,4.551L183.808,53.807C184.513,54.212 185.108,54.949 185.568,55.739C186.024,56.525 186.369,57.399 186.369,58.204L186.369,156.709C186.369,157.513 186.024,158.388 185.568,159.173C185.108,159.964 184.513,160.701 183.808,161.105L97.95,210.358C96.531,211.173 94.209,211.172 92.79,210.359L71.647,198.23L62.506,192.987L6.93,161.106C6.225,160.701 5.629,159.964 5.17,159.173C4.714,158.388 4.369,157.513 4.369,156.709L4.369,58.204C4.369,57.399 4.714,56.525 5.17,55.739C5.629,54.949 6.225,54.212 6.93,53.807L92.79,4.551Z"/>
        </mask>
        <filter id="testimonial-image-shadow" colorInterpolationFilters="sRGB">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.5"/>
        </filter>

        <g filter="url(#testimonial-image-shadow)">
            <g transform="matrix(1,0,0,1,4.63115,2.54522)" mask="url(#testimonial-image-mask)">
                <image x="0" y="0" width="190" height="215" xlinkHref={props.imageUrl}></image>
            </g>

            <g transform="matrix(1,0,0,1,4.63115,2.54522)" fill='none' stroke='#fff' strokeWidth={8}>
                <path d="M185.798,50.338C188.312,51.78 190.369,55.319 190.369,58.204L190.369,156.709C190.369,159.593 188.312,163.133 185.798,164.575L99.941,213.828C97.428,215.27 93.314,215.27 90.8,213.828L69.657,201.7C67.143,200.259 63.029,197.899 60.515,196.457L4.939,164.575C2.426,163.133 0.369,159.593 0.369,156.709L0.369,58.204C0.369,55.319 2.426,51.78 4.939,50.338L90.8,1.082C93.314,-0.361 97.428,-0.361 99.941,1.082L185.798,50.338Z"/>
            </g>
        </g>
    </svg>;
}

const TestimonialImage = styled(UnstyledTestimonialImage)``;

const TestimonialImageNameContainer = styled.div<{noImage?: boolean | undefined}>`
    position: relative;
    margin-bottom: ${(props) => props.noImage ? '4.9rem' : '2.5rem'};
    margin-top: ${(props) => props.noImage ? '2.6rem' : '0'};

    svg {
        position: relative;
        width: 10rem;
        height: 11rem;
    }

    ${NameTag} {
        ${(props) => !props.noImage && css`
            position: absolute;
            left: 9.3rem;
            top: 2.6rem;
        `}
    }
`;

function TestimonialBox(props: Testimonial & {className?: string | undefined}) {
    return <div className={props.className}>

        <TestimonialImageNameContainer noImage={!props.imageUrl}>
            <NameTag>{props.name}</NameTag>
            {props.imageUrl && <TestimonialImage imageUrl={props.imageUrl} />}
        </TestimonialImageNameContainer>

        <ArrowContentBox>
            <ArrowContentBoxInner>
                {props.quotes.map((quote) => <ArrowContentBoxQuote key={quote}>{quote}</ArrowContentBoxQuote>)}

                <ArrowContentBoxFooter>
                    {props.job}
                </ArrowContentBoxFooter>
            </ArrowContentBoxInner>
        </ArrowContentBox>
    </div>;
}

const StyledTestimonialBox = styled(TestimonialBox)<{index: number}>`
    flex: 1;

    ${NameTag} {
        animation: ${(props) => css`${defaultFadeInAnimation(props.index * 0.15 + 0.1)}, ${defaultSlideLeftAnimation(props.index * 0.15 + 0.1)}`};
    }

    ${TestimonialImage} {
        animation: ${(props) => css`${defaultFadeInAnimation(props.index * 0.1)}, ${defaultScaleUpAnimation(props.index * 0.1)}`};
    }

    ${ArrowContentBox} {
        animation: ${(props) => css`${defaultFadeInAnimation(props.index * 0.15 + 0.2)}, ${defaultSlideUpAnimation(props.index * 0.15 + 0.2)}`};
    }
`;

const TestimonialsPageContainer = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: center;
`;

const TestimonialsContainer = styled.div`
    display: flex;
    margin: 0 10rem;
    margin-bottom: 1rem;
    min-height: 26rem;
    gap: 5rem;
`;

const ReferencesHeadline = styled.div`
    display: flex;
    gap: 2rem;
    align-items: center;
    color: ${COLOR_GREY};
    margin-bottom: 1rem;

    &:before, &:after {
        content: '';
        display: block;
        border-bottom: dotted 2px ${COLOR_GREY_BRIGHTER};
        flex: 1;
    }
`;

const References = styled.div`
    display: flex;
    gap: 3rem;
    padding: 0 3rem;
    width: 100%;
    box-sizing: border-box;
    justify-content: center;
    color: ${COLOR_CI_BLUE};

    span {
        text-align: center;
    }
`;

const PageContainer = styled(MainContainer)`
    #references-container {
        animation: ${defaultFadeInAnimation()}, ${defaultSlideUpAnimation()};
    }
`;

export function TestimonialsPage(props: {
    page: Pages;
    onChangePage: (page: Pages) => void;
}) {
    const [language] = useLanguage();
    const page = language.testimonialsPage;
    return <PageContainer>
        <PageTitle width={page.headlineWidth}>{page.headline}</PageTitle>

        <TestimonialsPageContainer>
            <TestimonialsContainer>
                {page.testimonials.map((testimonial, index) => <StyledTestimonialBox key={testimonial.name} index={index} {...testimonial} />)}
            </TestimonialsContainer>

            <div id='references-container'>
                <ReferencesHeadline>{page.footer.headline}</ReferencesHeadline>
                <References>
                    {page.footer.companies.map((company) => <span key={company}>{company}</span>)}
                </References>
            </div>
        </TestimonialsPageContainer>

        <FooterContainer>
            <LandingpageButton
                width={340}
                onClick={() => props.onChangePage(Pages.LANDINGPAGE)}
                arrow='left'
            >
                {language.backToLandingPageLabel}
            </LandingpageButton>
        </FooterContainer>
    </PageContainer>;
}
